import { Stack, Text } from "@certn/cascadia";
import { splitLabelHeader } from "@certn/api";
import { Divider, SectionSubHeading } from "@certn/cascadia-react";
import { useState } from "react";
import {
  CustomTemplateResponsePreview,
  type QuestionPreviewShape,
} from "./CustomTemplateResponsePreview";
import { useTranslation } from "react-i18next";

type CustomTemplateQuestionPreviewProps = {
  question: QuestionPreviewShape;
  /**
   * 1-based position prefix shown before the question text (e.g. "1." or "2.").
   * Parents are responsible for choosing a numbering scheme so this component
   * never has to interpret display_priority, which is a raw ordering integer
   * that includes hidden/conditional rows. Pass ``null`` to render without a
   * number prefix \u2014 useful for conditional follow-up questions that visually
   * belong to their parent above.
   */
  displayNumber: number | null;
  /**
   * Conditional follow-up questions to render INSIDE this question's
   * collapsible body, after the main response preview. Use this to nest
   * "Shown when X = Yes" rows under their parent so a single chevron
   * controls the parent + its follow-ups together. Defaults to none, in
   * which case follow-ups are expected to be rendered as siblings by the
   * caller (the legacy flat layout).
   */
  followUps?: QuestionPreviewShape[];
};

export const CustomTemplateQuestionPreview = ({
  question,
  displayNumber,
  followUps = [],
}: CustomTemplateQuestionPreviewProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(true);

  const hasConditions = question.display_conditions.length > 0;
  const hasFollowUps = followUps.length > 0;

  // When follow-ups are nested inside this question's body, the chevron and
  // the visible follow-up section already convey "there's more here", so we
  // skip the detail tag entirely. Otherwise (legacy/flat layout) keep the
  // existing "(Conditional)" / "(Optional)" hints since they're the only
  // visible signal that distinguishes rows in the flat list.
  const endText = hasFollowUps
    ? undefined
    : hasConditions
      ? t("(Conditional)")
      : question.is_optional
        ? t("(Optional)")
        : undefined;

  const { fieldLabel } = splitLabelHeader(question.external_name);

  const title =
    displayNumber === null ? fieldLabel : `${displayNumber}. ${fieldLabel}`;

  return (
    <Stack gap="space-4" testID="custom-question-preview">
      <SectionSubHeading
        testID="custom-question-preview-subheading"
        title={title}
        isCollapsible
        noWrap
        isOpen={open}
        detailText={endText}
        onClick={() => setOpen(!open)}
      />
      {open && (
        <Stack gap="space-4">
          <CustomTemplateResponsePreview {...question} />
          {followUps.map((followUp) => (
            <Stack
              key={followUp.id}
              gap="space-3"
              testID="custom-question-preview-follow-up"
            >
              <Divider />
              <Text style="card/title-medium" color="text-weak">
                {t("Follow-up")}
              </Text>
              <CustomTemplateResponsePreview {...followUp} isNestedFollowUp />
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};
